/**
 * Google Drive for Excalidraw boards: the Share panel (Drive folder, who has
 * access, view-only share link) and the full-size snapshot the server wraps
 * into the board's Drive PDF. See eval/MIRO_PLAN.md "Drive-based access".
 */

import { exportToBlob } from '@excalidraw/excalidraw';
import { useEffect, useState } from 'react';
import { boardDriveApi, SnapshotThrottle } from '../../shared/src/boardDrive';
import { BoardSharePanel } from '../../shared/src/components/BoardDrive';
import { api, normalizeBoard, type Board } from './boardsApi';
import { sceneHash, type Snapshot } from './exporting';
import { toast } from './ui';

export const driveApi = boardDriveApi(api);

// ---- Share panel ------------------------------------------------------------------------

const SHARE_EVENT = 'xb:share';

/** Open the Share panel for a board (BoardPage's Share button). */
export function openShare(boardId: string): void {
  window.dispatchEvent(new CustomEvent(SHARE_EVENT, { detail: boardId }));
}

/** Mounted once by App: shows the Share panel when asked. */
export function ShareHost() {
  const [board, setBoard] = useState<Board | null>(null);
  useEffect(() => {
    const onShare = (e: Event) => {
      const id = (e as CustomEvent<string>).detail;
      api
        .json<Parameters<typeof normalizeBoard>[0]>(`/api/boards/${id}/`)
        .then((b) => setBoard(normalizeBoard(b)))
        .catch((err: Error) => toast(`Couldn't open sharing: ${err.message}`, 'error'));
    };
    window.addEventListener(SHARE_EVENT, onShare);
    return () => window.removeEventListener(SHARE_EVENT, onShare);
  }, []);
  if (!board) return null;
  return (
    <BoardSharePanel
      api={driveApi}
      boardId={board.id}
      role={board.access_role}
      shareEnabled={board.share_enabled}
      joinUrl={(token) => `${location.origin}/join/${token}`}
      onChanged={(c) => c.drive && toast('Saved to the Drive folder — its sharing now decides who can open the board', 'info')}
      onClose={() => setBoard(null)}
    />
  );
}

// ---- Drive snapshot ---------------------------------------------------------------------

const throttles = new Map<string, SnapshotThrottle>();

/** Large light-mode PNG of the whole board for the Drive PDF (≤ 4096 px). */
export async function snapshotImage(s: Snapshot): Promise<Blob | null> {
  if (!s.elements.length) return null;
  return exportToBlob({
    elements: s.elements,
    appState: { ...s.appState, exportBackground: true, exportWithDarkMode: false, viewBackgroundColor: s.appState.viewBackgroundColor || '#ffffff', exportScale: 1 },
    files: s.files,
    mimeType: 'image/png',
    maxWidthOrHeight: 4096,
    exportPadding: 48,
  });
}

/**
 * Upload the board snapshot when it changed (at most every 2 minutes unless
 * `force`). Called next to the thumbnail upload; failures are quiet — the
 * next change retries.
 */
export async function uploadDriveSnapshot(boardId: string, s: Snapshot, opts: { force?: boolean } = {}): Promise<void> {
  let t = throttles.get(boardId);
  if (!t) throttles.set(boardId, (t = new SnapshotThrottle()));
  const h = sceneHash(s);
  if (!t.due(h, opts.force)) return;
  t.mark(h);
  try {
    const blob = await snapshotImage(s);
    if (blob) await driveApi.uploadSnapshot(boardId, blob);
  } catch {
    t.reset();
  }
}
